import { Controller } from "@hotwired/stimulus"

// Drives the Communication Scripts page (app/views/scripts/index.html.erb).
//
// Behavior:
//  - selectType: when a script-type radio is chosen, enable the submit button
//    and highlight the selected type option label.
//  - On form submit (Turbo Stream): show the spinner, swap the button text to
//    "Generating...", and disable the submit button. When the Turbo response
//    comes back (and re-renders the #script-result target), restore the button.
//
// The form is wired with `data: { turbo_stream: true }`, so Turbo performs the
// POST and swaps the `result` target itself. We hook the form's Turbo lifecycle
// events for the loading state rather than issuing a manual fetch.
export default class extends Controller {
  static targets = ["form", "typeOption", "submitButton", "spinner", "buttonText", "result"]

  static classes = ["selected"]

  // Fallback highlight styling if no `data-script-selected-class` is supplied.
  static SELECTED_CLASSES = ["border-clay-600", "bg-clay-50", "ring-1", "ring-clay-600"]

  static GENERATING_TEXT = "Generating..."

  connect() {
    // Preserve the original button label so we can restore it after submit.
    if (this.hasButtonTextTarget) {
      this.originalButtonText = this.buttonTextTarget.textContent
    }

    if (this.hasFormTarget) {
      this.onSubmitStart = this.onSubmitStart.bind(this)
      this.onSubmitEnd = this.onSubmitEnd.bind(this)
      this.formTarget.addEventListener("turbo:submit-start", this.onSubmitStart)
      this.formTarget.addEventListener("turbo:submit-end", this.onSubmitEnd)
    }

    // Reflect any already-checked radio (e.g. after a back/forward restore).
    this.refreshSelection()
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("turbo:submit-start", this.onSubmitStart)
      this.formTarget.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    }
  }

  // Action: bound to `script#selectType` on each radio button's change event.
  selectType(event) {
    this.refreshSelection(event?.target)
  }

  refreshSelection(changedRadio = null) {
    const radio = changedRadio && changedRadio.checked
      ? changedRadio
      : this.element.querySelector('input[type="radio"]:checked')

    // Enable the submit button once any type is selected.
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !radio
    }

    // Highlight the chosen type option label, clearing the others.
    this.typeOptionTargets.forEach((option) => {
      const isSelected = !!radio && option.contains(radio)
      this.selectedClassList.forEach((cls) => {
        option.classList.toggle(cls, isSelected)
      })
    })
  }

  onSubmitStart() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
    }

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = this.constructor.GENERATING_TEXT
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

  onSubmitEnd() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
    }

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = this.originalButtonText ?? "Generate Script"
    }

    // Re-enable based on whether a type is still selected after the swap.
    this.refreshSelection()
  }

  // Resolve highlight classes: use the declared `selected` class if present,
  // otherwise fall back to the built-in Tailwind classes.
  get selectedClassList() {
    if (this.hasSelectedClass) {
      return this.selectedClasses
    }
    return this.constructor.SELECTED_CLASSES
  }
}
